// Admin erasure (PLAN §4 `erasures`, §11 "Admin erasure"; task 44). A hard delete for mistakes, legal takedowns and
// data-protection requests: a document or an entity is purged from D1, R2, the word and trigram indexes, Vectorize
// and the caches; claims supported only by an erased document go with it. The `erasures` log keeps what was purged,
// by whom, why and when, never the content itself. Backups age out within 30 days (runbook).

import type { AnswerSegment, ChatCaveat, ChatCitation, ClaimCitation, EntityCitation, PassageCitation } from './chat';

export const ERASURE_KINDS = ['document', 'entity'] as const;
export type ErasureKind = (typeof ERASURE_KINDS)[number];

export const ERASURE_REASONS = ['mistake', 'legal_takedown', 'data_protection', 'other'] as const;
export type ErasureReason = (typeof ERASURE_REASONS)[number];

/**
 * Reasons the log can show. `test_data` is never offered to an admin: only the staging smoke's own clean-up writes it,
 * when it erases the fixtures and the entity it made (D367). Older rows keep the four admin reasons.
 */
export const ERASURE_LOG_REASONS = [...ERASURE_REASONS, 'test_data'] as const;
export type ErasureLogReason = (typeof ERASURE_LOG_REASONS)[number];

export const ERASURE_REASON_LABELS: Record<ErasureLogReason, string> = {
  mistake: 'Uploaded or added by mistake',
  legal_takedown: 'Legal takedown',
  data_protection: 'Data-protection request',
  other: 'Other',
  test_data: 'Test data (staging smoke clean-up)',
};

/** What is written in place of anything an erased item leaves behind in saved chat answers and findings. */
export const ERASED_PLACEHOLDER = 'Erased by an admin';
/** A chat answer sentence that relied only on erased sources. */
export const ERASED_SENTENCE = '[Removed: this relied on an erased source.]';

export const ERASURE_LIMITS = {
  /** The note explaining why (kept in the log). */
  noteMax: 1000,
  /** The name the item is listed under in the log. */
  labelMax: 200,
  /** Entities left with nothing, erased together with a document. */
  orphansMax: 200,
} as const;

/** What an erasure removed (counts only: the log never keeps the content). */
export interface ErasureCounts {
  /** Documents: stored files (original and derived) deleted from R2. */
  files: number;
  /** Library passages (with their word-index entries and vectors). */
  passages: number;
  /** Claims deleted because nothing else supports them (for an entity: every claim about or pointing to it). */
  claims: number;
  /** Evidence rows removed from claims that other sources still support. */
  evidence: number;
  /** Names removed (entities), with their search entries and vectors. */
  names: number;
  /** Mentions of the entity in Library passages, or suggested mentions in the document. */
  mentions: number;
  /** Saved chat answers and findings that cited the item and were redacted. */
  redacted: number;
}

export const emptyErasureCounts = (): ErasureCounts => ({
  files: 0,
  passages: 0,
  claims: 0,
  evidence: 0,
  names: 0,
  mentions: 0,
  redacted: 0,
});

/** An entity that would have no claims left once a document is erased (offered for erasure with it). */
export interface OrphanEntity {
  id: string;
  code: string;
  name: string;
  kind: string;
}

/** What erasing an item would remove, shown before the admin confirms. */
export interface ErasurePreview {
  kind: ErasureKind;
  id: string;
  /** The name it will be listed under in the log unless the admin changes it. */
  label: string;
  counts: ErasureCounts;
  /** Documents: claims that stay because other sources support them (only this document's evidence goes). */
  claimsKept: number;
  /** Entities: other entities merged into it, erased with it. */
  mergedEntities: number;
  /** Documents: entities left with no claims, erased too unless unticked. */
  orphans: OrphanEntity[];
  /** A document still being processed is stopped first. */
  processing: boolean;
}

export interface ErasureInput {
  kind: ErasureKind;
  id: string;
  reason: ErasureReason;
  note?: string;
  /** The name to keep in the log (defaults to the file name or the entity's name). */
  label?: string;
  /** Documents: orphan entities to erase with it. */
  orphanIds?: string[];
}

export interface ErasureEntry {
  id: string;
  kind: ErasureKind;
  targetId: string;
  label: string;
  reason: ErasureLogReason;
  note: string | null;
  counts: ErasureCounts;
  /** An entity erased together with a document: that document's erasure. */
  parentId: string | null;
  erasedBy: { id: string | null; name: string | null; email: string | null };
  createdAt: number;
}

export type ErasureValidation =
  | { ok: true; value: Required<Omit<ErasureInput, 'note' | 'label'>> & { note: string | null; label: string | null } }
  | { ok: false; message: string };

export function validateErasureInput(raw: unknown): ErasureValidation {
  const v = (raw ?? {}) as Record<string, unknown>;
  if (!(ERASURE_KINDS as readonly unknown[]).includes(v.kind)) return { ok: false, message: 'Choose a document or an entity to erase.' };
  if (typeof v.id !== 'string' || !v.id.trim()) return { ok: false, message: 'Say which item to erase.' };
  if (!(ERASURE_REASONS as readonly unknown[]).includes(v.reason)) return { ok: false, message: 'Choose why it is being erased.' };
  const note = typeof v.note === 'string' ? v.note.trim() : '';
  if (note.length > ERASURE_LIMITS.noteMax) return { ok: false, message: `Keep the note under ${ERASURE_LIMITS.noteMax} characters.` };
  if (v.reason === 'other' && !note) return { ok: false, message: 'Say why it is being erased.' };
  const label = typeof v.label === 'string' ? v.label.trim() : '';
  if (label.length > ERASURE_LIMITS.labelMax) return { ok: false, message: `Keep the name under ${ERASURE_LIMITS.labelMax} characters.` };
  const orphanIds = Array.isArray(v.orphanIds) ? v.orphanIds.filter((x): x is string => typeof x === 'string') : [];
  if (orphanIds.length > ERASURE_LIMITS.orphansMax) return { ok: false, message: 'Too many entities to erase at once.' };
  if (v.kind !== 'document' && orphanIds.length) return { ok: false, message: 'Only a document erasure can take entities with it.' };
  return {
    ok: true,
    value: {
      kind: v.kind as ErasureKind,
      id: v.id.trim(),
      reason: v.reason as ErasureReason,
      note: note || null,
      label: label || null,
      orphanIds: [...new Set(orphanIds)],
    },
  };
}

export function addErasureCounts(a: ErasureCounts, b: Partial<ErasureCounts>): ErasureCounts {
  const out = { ...a };
  for (const k of Object.keys(out) as (keyof ErasureCounts)[]) out[k] += b[k] ?? 0;
  return out;
}

/** "3 passages, 2 claims…" (only what is not zero). */
export function describeErasureCounts(c: ErasureCounts): string {
  const parts: [number, string, string][] = [
    [c.files, 'stored file', 'stored files'],
    [c.passages, 'passage', 'passages'],
    [c.claims, 'claim', 'claims'],
    [c.evidence, 'piece of evidence', 'pieces of evidence'],
    [c.names, 'name', 'names'],
    [c.mentions, 'mention', 'mentions'],
    [c.redacted, 'saved answer redacted', 'saved answers redacted'],
  ];
  const out = parts.filter(([n]) => n > 0).map(([n, one, many]) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`);
  return out.length ? out.join(', ') : 'Nothing else';
}

// ── Saved answers ────────────────────────────────────────────────────────────────────────────────────────────

/** Whether a citation points at something in `gone` (erased document, passage, claim or entity ids). */
export function citesErased(c: ChatCitation, gone: ReadonlySet<string>): boolean {
  if (c.erased) return false;
  if (c.kind === 'passage') return gone.has(c.documentId) || gone.has(c.chunkId);
  if (c.kind === 'claim') return gone.has(c.claimId) || gone.has(c.entityId);
  return gone.has(c.entityId);
}

/** The placeholder a citation becomes: its ref and kind stay (the answer's markers still resolve), nothing else. */
export function erasedCitation(c: ChatCitation): ChatCitation {
  if (c.kind === 'passage')
    return {
      ref: c.ref,
      kind: 'passage',
      erased: true,
      chunkId: '',
      documentId: '',
      filename: ERASED_PLACEHOLDER,
      title: null,
      excerpt: '',
      lang: null,
      sensitivity: c.sensitivity,
      collections: [],
      unverified: false,
      guide: null,
    } satisfies PassageCitation;
  if (c.kind === 'claim')
    return {
      ref: c.ref,
      kind: 'claim',
      erased: true,
      claimId: '',
      entityId: '',
      entityName: ERASED_PLACEHOLDER,
      text: ERASED_PLACEHOLDER,
      status: c.status,
      claimKind: c.claimKind,
      attributionLevel: c.attributionLevel,
      sensitivity: c.sensitivity,
    } satisfies ClaimCitation;
  return {
    ref: c.ref,
    kind: 'entity',
    erased: true,
    entityId: '',
    name: ERASED_PLACEHOLDER,
    code: '',
    entityKind: c.entityKind,
    status: c.status,
    sensitivity: c.sensitivity,
  } satisfies EntityCitation;
}

interface Redactable {
  segments: AnswerSegment[];
  citations: ChatCitation[];
  caveats?: ChatCaveat[];
}

/**
 * Redacts one saved answer of erased items: their citations become placeholders, a sentence whose every source was
 * erased is replaced (it may repeat the erased content), and erased reading guides are dropped. Returns null when
 * nothing in the answer pointed at them.
 */
export function redactAnswer<A extends Redactable>(a: A, gone: ReadonlySet<string>): A | null {
  const hit = new Set(a.citations.filter(c => citesErased(c, gone)).map(c => c.ref));
  const guideHit = (a.caveats ?? []).some(c => c.guides?.some(g => gone.has(g.documentId)));
  const passageGuideHit = a.citations.some(c => c.kind === 'passage' && !!c.guide && gone.has(c.guide.documentId));
  if (!hit.size && !guideHit && !passageGuideHit) return null;
  const erased = new Set([...hit, ...a.citations.filter(c => c.erased).map(c => c.ref)]);
  return {
    ...a,
    citations: a.citations.map(c => {
      if (hit.has(c.ref)) return erasedCitation(c);
      if (c.kind === 'passage' && c.guide && gone.has(c.guide.documentId)) return { ...c, guide: null };
      return c;
    }),
    segments: a.segments.map(s => (s.cites.length && s.cites.every(r => erased.has(r)) ? { ...s, text: ERASED_SENTENCE } : s)),
    ...(a.caveats ? { caveats: a.caveats.map(c => (c.guides ? { ...c, guides: c.guides.filter(g => !gone.has(g.documentId)) } : c)) } : {}),
  };
}

/** Findings keep their text (people edit and sign it off); only the cited claims become placeholders. */
export function redactClaimCitations(citations: ClaimCitation[], gone: ReadonlySet<string>): ClaimCitation[] | null {
  if (!citations.some(c => citesErased(c, gone))) return null;
  return citations.map(c => (citesErased(c, gone) ? (erasedCitation(c) as ClaimCitation) : c));
}
